import { Button, ButtonVariants, Form, FormSection, Grid, Switch, TextInput } from "@centricsoftware/design-system";
import { useSetRefOnMount } from "csi/components/hooks";
import { observer } from "mobx-react-lite";
import React, { FunctionComponent } from "react";
import AggregateStoreAlternativeStore from "./AggregateStoreAlternativeStore";
import AggregateStoreGroup from "./AggregateStoreGroup";
import css from "./AggregateStoreInfo.module.css";
import { useAggregateStoreInfoStore } from "./AggregateStoreInfoStore";

const borderStyles = {
	border: "1px solid var(--cds-grays-gray700)",
	borderRadius: "3px",
	padding: "8px",
};

interface AggregateStoreAlternativeProps {
	alternative: AggregateStoreAlternativeStore;
}

const AggregateStoreAlternative: FunctionComponent<AggregateStoreAlternativeProps> = observer(function AggregateStoreAlternative({ alternative }) {
	const { readOnly } = useAggregateStoreInfoStore();
	const {
		canRemoveAlternative,
		condition,
		conditionError,
		description,
		isConditionRequired,
		onChangeCondition,
		onChangeDescription,
		onClickRemoveAlternative,
	} = alternative;
	
	return (
		<Grid
			className={css.container}
			container
			direction="column"
			isFullWidth
			spacing={8}
		>
			<FormSection title="Aggregate Store Alternative" />
			{canRemoveAlternative && (
				<Button
					childrenRes={{ res: "AggregateStoreRemoveAlternative" }}
					onClick={onClickRemoveAlternative}
					variant={ButtonVariants.secondary}
				/>
			)}
			{(!readOnly || !!description) && (
				<TextInput
					labelRes={{ res: "AggregateStoreDefinition.Attr.Description" }}
					onBlurChange={onChangeDescription}
					readOnly={readOnly}
					value={description}
				/>
			)}
			{(!readOnly || !!condition) && (
				<TextInput
					error={!!conditionError}
					errorMessage={conditionError}
					labelRes={{ res: "AggregateStoreAlternative.Attr.Condition" }}
					onBlurChange={onChangeCondition}
					readOnly={readOnly}
					required={isConditionRequired}
					value={condition}
				/>
			)}
			<AggregateStoreGroups alternative={alternative}  />
		</Grid>
	);
});

const AggregateStoreGroups: FunctionComponent<AggregateStoreAlternativeProps> = observer(function AggregateStoreGroups({ alternative }) {
	const {
		canAddGroup,
		dynamic,
		groups,
		isValid,
		onClickAddGroup,
	} = alternative;
	
	return (
		<>
			{groups.map((group, index) => (
				<Grid
					className={css.container}
					container
					dataCsiAutomation="AggregateStoreGroup"
					direction="column"
					isFullWidth
					justifyContent="space-between"
					key={`group-${index}`}
					spacing={8}
				>
					<AggregateStoreGroup group={group} />
				</Grid>
			))}
			{canAddGroup && (
				<Button
					childrenRes={{ res: dynamic ? "AggregateStoreDynamicAddGroup" : "AggregateStoreAddGroup" }}
					disabled={!isValid}
					onClick={onClickAddGroup}
					variant={ButtonVariants.secondary}
				/>
			)}
		</>
	);
});

const AggregateStoreInfo: FunctionComponent = observer(function AggregateStoreInfo() {
	const {
		alternatives,
		canAddAlternative,
		canOverrideAggregateStore,
		definition: {
			sourceClass,
			targetClass,
		},
		dynamic,
		onChangeOverride,
		onClickAddAlternative,
		overridden,
		showGroupsOnly,
		setFormRef,
	} = useAggregateStoreInfoStore();

	const formRef = useSetRefOnMount<HTMLFormElement>(setFormRef);

	return (
		<Form isFullWidth ref={formRef}>
			{dynamic && (
				<FormSection
					descriptionRes={{ res: "AggregateStoreDynamicDesc" }}
					titleRes={{ res: "AggregateStoreDynamic" }}
				/>
			)}
			<TextInput
				labelRes={{ res: "SourceClass" }}
				readOnly
				value={sourceClass}
			/>
			<TextInput
				labelRes={{ res: "TargetClass" }}
				readOnly
				value={targetClass}
			/>
			{canOverrideAggregateStore && (
				<Switch
					checked={overridden}
					labelRes={{ res: "AggregateStoreOverrideAggregateStore" }}
					onChange={onChangeOverride}
				/>
			)}
			{showGroupsOnly ? (
				<AggregateStoreGroups alternative={alternatives[0]} />
			) : (
				<>
					{alternatives.map((alternative, index) => (
						<AggregateStoreAlternative
							alternative={alternative}
							key={`alternative-${index}`}
						/>
					))}
					{overridden && (
						<Button
							childrenRes={{ res: "AggregateStoreAddAlternative" }}
							disabled={!canAddAlternative}
							onClick={onClickAddAlternative}
							variant={ButtonVariants.secondary}
						/>
					)}
				</>
			)}
		</Form>
	);
});

export default AggregateStoreInfo;